/**
 * hlsThumbnail — generate thumbnails from an HLS (`.m3u8`) stream WITHOUT
 * downloading the whole source video.
 *
 * When a video has a `transcoded_url` that's an HLS playlist, we can grab just a
 * few small media segments (`.ts` / `.m4s`, a few seconds each) spread across the
 * timeline and decode one frame from each. That's a few hundred KB total instead
 * of the full multi-hundred-MB file.
 *
 * Decoding a raw segment: Safari can play MPEG-TS in a <video> element, but Chrome
 * can't — so we decode segments with ffmpeg.wasm (which handles TS and fMP4). For
 * fragmented-MP4 playlists the fragments aren't self-contained, so we prepend the
 * `#EXT-X-MAP` init segment before feeding wasm.
 *
 * Every path is wrapped so any failure resolves to [] — the caller then falls back
 * to its whole-file path. It can never break other flows.
 */

import { generateFirstFrameWithWasm } from './wasmThumbnail';

export function isHlsUrl(url) {
  return typeof url === 'string' && url.split('?')[0].toLowerCase().endsWith('.m3u8');
}

function resolveUrl(base, rel) {
  try { return new URL(rel, base).href; } catch (e) { return rel; }
}

async function fetchText(url) {
  const r = await fetch(url);
  if (!r.ok) throw new Error(`HLS playlist fetch failed (${r.status})`);
  return r.text();
}

// A master playlist points at variant media playlists via #EXT-X-STREAM-INF.
// Return the first variant's (absolute) URL, or null if this is already a media
// playlist.
function firstVariant(text, playlistUrl) {
  const lines = text.split(/\r?\n/);
  for (let i = 0; i < lines.length; i += 1) {
    if (lines[i].trim().startsWith('#EXT-X-STREAM-INF')) {
      for (let j = i + 1; j < lines.length; j += 1) {
        const t = lines[j].trim();
        if (t && !t.startsWith('#')) return resolveUrl(playlistUrl, t);
      }
    }
  }
  return null;
}

// The fMP4 init segment (needed to decode individual .m4s fragments), or null.
function parseInitSegment(text, playlistUrl) {
  const m = text.match(/#EXT-X-MAP:[^\r\n]*URI="([^"]+)"/);
  return m ? resolveUrl(playlistUrl, m[1]) : null;
}

// All media segment URIs (absolute), in playlist order.
function parseSegments(text, playlistUrl) {
  const segs = [];
  text.split(/\r?\n/).forEach((line) => {
    const t = line.trim();
    if (t && !t.startsWith('#')) segs.push(resolveUrl(playlistUrl, t));
  });
  return segs;
}

// Evenly-spaced indices so the frames span the whole video, not just the start.
function pickSpread(total, count) {
  const n = Math.min(count, total);
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const idx = Math.floor(((i + 0.5) / n) * total);
    const clamped = Math.min(idx, total - 1);
    if (!out.includes(clamped)) out.push(clamped);
  }
  return out;
}

/**
 * Generate up to `count` thumbnails (data URLs) by sampling HLS segments.
 * Returns [] on any failure so the caller can fall back to the whole-file path.
 */
export async function generateThumbnailsFromHls(m3u8Url, options = {}) {
  const count = options.count || 6;
  try {
    if (!isHlsUrl(m3u8Url)) return [];

    // Resolve master -> media playlist if needed.
    let mediaUrl = m3u8Url;
    let text = await fetchText(m3u8Url);
    const variant = firstVariant(text, m3u8Url);
    if (variant) {
      mediaUrl = variant;
      text = await fetchText(variant);
    }

    const segments = parseSegments(text, mediaUrl);
    if (!segments.length) return [];

    // fMP4 fragments need the init segment prepended to be decodable.
    const initUrl = parseInitSegment(text, mediaUrl);
    let initBuf = null;
    if (initUrl) {
      try {
        const ir = await fetch(initUrl);
        if (ir.ok) initBuf = await ir.arrayBuffer();
      } catch (e) { /* ignore — try segments as-is */ }
    }

    const picks = pickSpread(segments.length, count);
    const frames = [];
    for (let i = 0; i < picks.length; i += 1) {
      try {
        // eslint-disable-next-line no-await-in-loop
        const r = await fetch(segments[picks[i]]);
        if (!r.ok) continue; // eslint-disable-line no-continue
        // eslint-disable-next-line no-await-in-loop
        const segBuf = await r.arrayBuffer();
        const decodable = initBuf ? new Blob([initBuf, segBuf]) : new Blob([segBuf]);
        // Grab the segment's first frame (reliable for short TS/fMP4 segments).
        // eslint-disable-next-line no-await-in-loop
        const url = await generateFirstFrameWithWasm(decodable);
        if (url) frames.push(url);
        else console.warn('[hlsThumb] segment decoded to no frame', `(${segBuf.byteLength} bytes)`);
      } catch (e) { console.warn('[hlsThumb] segment fetch/decode error', e); }
    }
    return frames;
  } catch (e) {
    return [];
  }
}

export default generateThumbnailsFromHls;
